import { isEmpty } from '../is';

export const stateDict = {
  OFFLINE: -1,
  NORMAL: 0,
  WARN: 1,
  ALARM: 2
};

export const runstateDict = {
  OFFLINE: 0,
  ONLINE: 1,
  FIX: 2,
  FAULT: 3,
  COLLECT_BASELINE: 4
};

/** @typedef {{ sn: string, id: string, name: string, state: number, state2: number, runstate: number, code: number, runMode: number, l2:number, c2: string, isAlarm?: boolean }} IBasicDeviceData */

// l: 优先级别  c: 颜色
// 传感器状态
const stateMap = new Map([
  [0, { v: 0, l: 1, l2: 3, c: '#03b36e', code: 'normal', t: '正常', ico: 'icon-correct' }],
  [
    1,
    { v: 1, l: 3, l2: 5, c: '#FF6100', code: 'alarm', t: '预警', isAlarm: true, ico: 'icon-circle' }
  ],
  [
    2,
    { v: 2, l: 3, l2: 5, c: '#FF4847', code: 'alarm', t: '报警', isAlarm: true, ico: 'icon-circle' }
  ]
]);

// 运行状态
const runstateMap = new Map([
  [
    -1,
    {
      v: -1,
      l: 5,
      l2: 1,
      c: '#a9a9a9',
      c2: '#949494',
      code: 'offline',
      isOffline: true,
      t: '离线',
      ico: 'icon-circle'
    }
  ],
  [
    0,
    {
      v: 0,
      l: 5,
      l2: 1,
      c: '#a9a9a9',
      c2: '#949494',
      code: 'offline',
      isOffline: true,
      t: '离线',
      ico: 'icon-circle'
    }
  ],
  [
    1,
    {
      v: 1,
      l: 1,
      l2: 3,
      c: '#03b36e',
      c2: '#03b36e',
      code: 'normal',
      t: '在线',
      ico: 'icon-correct'
    }
  ],
  [2, { v: 2, l: 3, l2: 4, c: '#ffd04f', c2: '#ffd04f', t: '维护', ico: 'icon-fault' }],
  [
    3,
    {
      v: 3,
      l: 3,
      l2: 4,
      c: '#ffbb00',
      c2: '#ffbb00',
      code: 'fault',
      t: '故障',
      ico: 'icon-warning'
    }
  ],
  [4, { v: 4, l: 3, l2: 4, c: '#00eeff', c2: '#00eeff', t: '采集基线', ico: 'icon-circle' }]
]);

const stateMap2 = new Map([
  [
    -1,
    {
      v: -1,
      l: 5,
      l2: 1,
      c: '#a9a9a9',
      c2: '#949494',
      code: 'offline',
      isOffline: true,
      t: '离线',
      ico: 'icon-on-line'
    }
  ],
  [
    0,
    { v: 0, l: 2, l2: 2, c: 'unset', c2: '#03b36e', code: 'normal', t: '正常', ico: 'icon-on-line' }
  ],
  [
    1,
    {
      v: 1,
      l: 4,
      l2: 5,
      c: '#FF6100',
      c2: '#FF6100',
      code: 'alarm',
      t: '预警',
      isAlarm: true,
      ico: 'icon-on-line'
    }
  ],
  [
    2,
    {
      v: 2,
      l: 4,
      l2: 5,
      c: '#FF4847',
      c2: '#FF4847',
      code: 'alarm',
      t: '报警',
      isAlarm: true,
      ico: 'icon-on-line'
    }
  ],
  [
    3,
    {
      v: 3,
      l: 4,
      l2: 5,
      c: '#9828BC',
      c2: '#9828BC',
      code: 'alarm',
      t: '报警',
      isAlarm: true,
      ico: 'icon-on-line'
    }
  ]
]);

/**
 * 根据设备状态状态和传感器状态返回颜色
 * @param {Number} runstate 设备运行状态
 * @param {Number} state 传感器状态
 * @returns ColorHex
 */
export function getColor(runstate, state) {
  return getOptions(runstate, state).c;
}

/**
 * 根据设备状态状态和传感器状态返回对应选项
 * @param {Number} runstate 设备运行状态
 * @param {Number} state 传感器状态
 */
export function getOptions(runstate, state) {
  const runstateOpt = getStateOption({ runstate });
  const stateOpt = getStateOption({ state2: state });
  const arr = [runstateOpt, stateOpt];
  return arr.sort((a, b) => b.l - a.l)[0];
}

/**
 * 根据设备状态状态和传感器状态返回对应选项
 * @param {Array<{state: number, state2: number, runstate: number}>} deviceList 设备运行状态
 */
export function getStateHighLevelOptionBy(deviceList, { useLevel2 = true }) {
  const arr = [];

  deviceList.forEach(e => {
    const opt1 = getOptions(e.runstate, e.state2);

    !isEmpty(opt1) && arr.push(opt1);
  });

  const prop = useLevel2 ? 'l2' : 'l';

  const list = arr.sort((a, b) => b[prop] - a[prop]);

  return list[0];
}

/**
 * 根据状态返回颜色
 * @param {Number} state 状态
 * @returns ColorHex
 */
export function getState2Color(state) {
  state = getStateOption({ state2: state });
  return state.c;
}

/**
 * 根据状态返回颜色
 * @param {Number} state 状态
 * @returns ColorHex
 */
export function getStateColor(state) {
  state = getStateOption({ state: state });
  return state.c;
}

/**
 * 根据运行状态返回颜色
 * @param {Number} runstate 状态
 * @returns ColorHex
 */
export function getRunstateColor(runstate) {
  runstate = getStateOption({ runstate });
  return runstate.c;
}

/**
 * 根据状态返回文本
 * @param {Number} state 状态
 * @returns {String}
 */
export function getStateText(state) {
  state = getStateOption({ state });
  return state.t;
}

/**
 * 根据运行状态返回文本
 * @param {Number} runstate 状态
 * @returns {String}
 */
export function getRunstateText(runstate) {
  runstate = getStateOption({ runstate });
  return runstate.t;
}

/**
 * 根据状态返回对应选项
 * @param {{ state: Number, runstate: Number }}
 * @returns {{ v: number; l: number; c: string; t: string; ico: string;}}
 */
export function getStateOption({ state, runstate, state2 }) {
  if (!isNaN(state)) {
    return stateMap.has(state) ? stateMap.get(state) : stateMap.get(0);
  } else if (!isNaN(runstate)) {
    return runstateMap.has(runstate) ? runstateMap.get(runstate) : runstateMap.get(0);
  } else if (!isNaN(state2)) {
    return stateMap2.has(state2) ? stateMap2.get(state2) : stateMap2.get(0);
  }
  return {};
}

/**
 * 设备状态 警告或报警
 * @param {number} state
 * @returns {boolean}
 */
export function isAlarmOrWarn(state) {
  return state > 0;
}

/**
 * 设备状态 报警
 * @param {number} state
 * @returns {boolean}
 */
export function isAlarm(state) {
  return state >= 2;
}

/**
 * 是否是正常状态
 * @param {number} state
 * @param {number} runstate
 * @returns {boolean}
 */
export function isNormal(state, runstate) {
  return [0, 1].includes(runstate) && state === 0;
}

/**
 * 是否是维修状态
 * @param {number} runstate
 * @returns {boolean}
 */
export function isFault(runstate) {
  return runstate === runstateDict.FAULT;
}

/**
 * 是否是维修或故障状态
 * @param {number} runstate
 * @returns {boolean}
 */
export function isFaultOrFix(runstate) {
  return runstate === runstateDict.FAULT || runstate === runstateDict.FIX;
}

/**
 * 设置设备状态为正常
 * @param {IBasicDeviceData} device
 */
export function normalizeState(device) {
  device.state = stateDict.NORMAL;
  device.state2 = stateDict.NORMAL;
}

/**
 * 设置设备运行状态为正常
 * @param {IBasicDeviceData} device
 */
export function normalizeRunstate(device) {
  device.runstate = runstateDict.ONLINE;
}

export function stateFormater(runstate, state) {
  state = isNaN(state) ? -1 : state;
  return isOnline(runstate) ? state : -1;
}

/**
 * 判断是在线状态
 * @param {number} runstate
 */
export function isOnline(runstate) {
  return runstate !== -1 && runstate !== 0;
}

/**
 * 判断是在线状态
 * @param {{state: number, runstate: number}} param0
 */
export function isOffline({ runstate, state }) {
  return getOptions(runstate, state).t === '离线';
}
